// Full implementation with original styling, route itinerary, and embedded map

import React, { useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import AsyncSelect from "react-select/async";
import { Table, Spinner } from "react-bootstrap";
import axios from "axios";
import "react-data-grid/lib/styles.css";
import "react-tooltip/dist/react-tooltip.css";
import LaneService from "../../services/LaneService";
import UserService from "../../services/UserService";
import {PageLoader} from "../PageLoader";
import StateBreakdownReportTable from "./StateBreakdownReportTable";
import RouteItineraryReportTable from "./RouteItineraryReportTable";
import MapComponent from "./MapComponent";
import RouteSummary from "./RouteSummary";
import { faMap, faSlidersH, faTruck, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import Select from "react-select";
import RouteSettings from "./RouteSettings";
import "../../styles/PlanRoute.css";




const tabIcons = {
    "Stops": faMap,
    "Configuration": faSlidersH,
    "Vehicle & Hazmat": faTruck,
    "Costs": faDollarSign,
};

const selectStyles = {
    container: (provided) => ({
        ...provided,
        width: "100%",
        // minWidth: "360px",
        maxWidth: "720px",
        fontFamily: "Overpass-Regular",
        position: "relative",
        zIndex: 1,
    }),
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? "#006838" : "#c0c0c0",
        boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,104,56, 0.25)" : null,
        "&:hover": { borderColor: "#028E7D" },
        borderRadius: "8px",
        padding: "8px",
        backgroundColor: "white",
        position: "relative",
        zIndex: 1,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        backgroundColor: "white",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        maxHeight: "300px",
        overflowY: "auto",
    }),
    menuList: (provided) => ({
        ...provided,
        padding: 0,
        margin: 0,
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? "#028E7D"
            : state.isFocused
                ? "rgba(2, 142, 125, 0.1)"
                : "white",
        color: state.isSelected ? "white" : "black",
        fontFamily: "Overpass-Regular",
        fontSize: "14px",
        padding: "12px 16px",
        cursor: "pointer",
        borderBottom: "1px solid #f0f0f0",
        "&:last-child": {
            borderBottom: "none",
        },
        "&:hover": {
            backgroundColor: state.isSelected ? "#028E7D" : "rgba(2, 142, 125, 0.1)",
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "#6c757d",
        fontSize: "14px",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "#495057",
        fontSize: "14px",
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: "#028E7D",
        "&:hover": { color: "#026d5f" },
    }),
};


const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hrs}h ${mins}m`;
};

const PlanRoute = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [stops, setStops] = useState([null]);
    const [isPlanning, setIsPlanning] = useState(false);
    const [routeResult, setRouteResult] = useState(null);
    const [hazmatType, setHazmatType] = useState([]);
    const [emailAddress, setEmailAddress] = useState("");
    const [recipientPhone, setRecipientPhone] = useState("+1");
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    // Handle hazmat change:
    const handleHazmatChange = (selectedOptions) => {
        setHazmatType(selectedOptions);
    };

    const [costParams, setCostParams] = useState({
        fuelPrice: "",
        fuelEconomy: "",
        maintenanceCost: "",
        laborCost: ""
    });

    const [fuelPrices, setFuelPrices] = useState([]);
    const [isLoadingFuel, setIsLoadingFuel] = useState(false);
    const [calculatedFuelPrice, setCalculatedFuelPrice] = useState("");
    const [subscriptionType, setSubscriptionType] = useState("");
    const [fuelApiError, setFuelApiError] = useState("");



    const [avoidOptions, setAvoidOptions] = useState({
        tolls: false,
        highways: false,
        dirtRoads: false,
        ferries: false,
        tunnels: false,
        uTurns: false,
        borderCrossings: true
    });


    const customStyles = {
        control: (base) => ({
            ...base,
            borderColor: "#ccc",
            boxShadow: "none",
            "&:hover": { borderColor: "#888" },
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: "#028E7D",
            color: "white",
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "white",
        }),
    };

    const hazmatOptions = [
        { value: "explosive", label: "Explosive" },
        { value: "gas", label: "Gas" },
        { value: "flammable", label: "Flammable" },
        { value: "combustible", label: "Combustible" },
        { value: "organic", label: "Organic" },
        { value: "poison", label: "Poison" },
        { value: "radioactive", label: "Radioactive" },
        { value: "corrosives", label: "Corrosives" },
        { value: "poisonousInhalation", label: "PIH" },
        { value: "other", label: "Other" },
    ];


    const vehicleOptions = [
        { value: { length: "double", height: 160, width: 102, weight: 80000, axleCount: 5 }, label: "Dry Van" },
        { value: { length: "double", height: 170, width: 102, weight: 82000, axleCount: 5 }, label: "Flatbed" },
        { value: { length: "single", height: 160, width: 102, weight: 75000, axleCount: 4 }, label: "Reefer" },

        { value: { length: "double", height: 163, width: 102, weight: 80000, axleCount: 5 }, label: "Auto Carrier" },
        { value: { length: "double", height: 175, width: 102, weight: 83000, axleCount: 6 }, label: "B-Train" },
        { value: { length: "single", height: 155, width: 96,  weight: 67000, axleCount: 3 }, label: "Box Truck" },
        { value: { length: "double", height: 172, width: 102, weight: 78000, axleCount: 5 }, label: "Conestoga" },

        { value: { length: "single", height: 162, width: 102, weight: 80000, axleCount: 3 }, label: "Cattle Trailer - 3 axle" },
        { value: { length: "single", height: 162, width: 102, weight: 80000, axleCount: 4 }, label: "Cattle Trailer - 4 axle" },

        { value: { length: "single", height: 150, width: 100, weight: 60000, axleCount: 4 }, label: "Container" },
        { value: { length: "single", height: 160, width: 100, weight: 72000, axleCount: 4 }, label: "Conveyor" },
        { value: { length: "double", height: 178, width: 102, weight: 86000, axleCount: 6 }, label: "Double Drop" },
        { value: { length: "double", height: 170, width: 102, weight: 79000, axleCount: 5 }, label: "Drop Deck" },

        { value: { length: "single", height: 170, width: 102, weight: 80000, axleCount: 2 }, label: "Dry Chassis - single" },
        { value: { length: "double", height: 170, width: 102, weight: 80000, axleCount: 2 }, label: "Dry Chassis - double" },

        { value: { length: "single", height: 158, width: 102, weight: 72000, axleCount: 4 }, label: "Dump Trailer" },
        { value: { length: "single", height: 165, width: 102, weight: 70000, axleCount: 4 }, label: "Hopper Bottom" },
        { value: { length: "double", height: 180, width: 102, weight: 85000, axleCount: 6 }, label: "Lowboy" },
        { value: { length: "double", height: 175, width: 102, weight: 80000, axleCount: 5 }, label: "Maxi" },
        { value: { length: "single", height: 155, width: 96,  weight: 65000, axleCount: 3 }, label: "Moving Van" },
        { value: { length: "single", height: 155, width: 102, weight: 70000, axleCount: 4 }, label: "Pneumatic" },
        { value: { length: "single", height: 160, width: 96,  weight: 65000, axleCount: 3 }, label: "Power Only" },
        { value: { length: "double", height: 160, width: 96,  weight: 75000, axleCount: 5 }, label: "Reefer Double" },
        { value: { length: "double", height: 190, width: 102, weight: 85000, axleCount: 6 }, label: "Removable Gooseneck" },
        { value: { length: "single", height: 140, width: 80,  weight: 50000, axleCount: 2 }, label: "Sprinter" },
        { value: { length: "double", height: 175, width: 102, weight: 80000, axleCount: 5 }, label: "Step Deck" },
        { value: { length: "single", height: 160, width: 96,  weight: 70000, axleCount: 4 }, label: "Straight Box Reefer" },
        { value: { length: "single", height: 160, width: 96,  weight: 68000, axleCount: 4 }, label: "Straight Box" },
        { value: { length: "double", height: 185, width: 102, weight: 83000, axleCount: 6 }, label: "Stretch Trailer" },        
        { value: { length: "single", height: 168, width: 102, weight: 78000, axleCount: 4 }, label: "Tanker" },
        { value: { length: "double", height: 170, width: 102, weight: 82000, axleCount: 5 }, label: "Truck and Trailer" },

        { value: { length: "single", height: 162, width: 102, weight: 25000, axleCount: 2 }, label: "Walking Floor - single" },
        { value: { length: "double", height: 162, width: 102, weight: 48000, axleCount: 2 }, label: "Walking Floor - double" },
    ];


    const [selectedVehicle, setSelectedVehicle] = useState(vehicleOptions[0]); // Default: Dry Van



    // Tabs state
    const [activeTab, setActiveTab] = useState("Stops");
    const tabs = ["Stops", "Configuration","Vehicle & Hazmat", "Costs"];

    const handleAddStop = () => setStops((prev) => [...prev, null]);

    const handleStopChange = (value, index) => {
        const updated = [...stops];
        updated[index] = value;
        setStops(updated);
    };

    const handleRemoveStop = (index) => {
        const updated = [...stops];
        updated.splice(index, 1);
        setStops(updated);
    };

    const loadOptions = useCallback(async (inputValue) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await LaneService.getCityOptionsFromMileMaker(inputValue, token);
            return response || [];
        } catch (error) {
            console.error("Failed to load city options:", error);
            return [];
        }
    }, [getAccessTokenSilently]);

    const hasCostParams = Object.values(costParams).some(
        (v) => v !== "" && v !== null && v !== undefined
    );

    const buildCostParameters = () => {
        const {
            fuelPrice,
            fuelEconomy,
            maintenanceCost,
            laborCost
        } = costParams;

        const hasFuel = fuelPrice !== "" && fuelEconomy !== "";
        const hasMaintenance = maintenanceCost !== "";
        const hasLabor = laborCost !== "";

        if (!hasFuel && !hasMaintenance && !hasLabor) {
            return null; // ❌ Don't include costParameters in payload
        }

        const result = {};
        if (hasFuel) {
            result.fuelPrice = parseFloat(fuelPrice);
            result.fuelEconomy = parseFloat(fuelEconomy);
        }
        if (hasMaintenance) {
            result.maintenanceCost = parseFloat(maintenanceCost);
        }
        if (hasLabor) {
            result.laborCost = parseFloat(laborCost);
        }

        return result;
    };




    const handlePlanRoute = async () => {
        setIsPlanning(true);
        setRouteResult(null);

        try {
            const token = await getAccessTokenSilently();

            // Convert avoidOptions (checkboxes) to API-friendly list
            const avoidMapping = {
                tolls: "tolls",
                highways: "highways",
                dirtRoads: "dirtRoads",
                ferries: "ferries",
                tunnels: "tunnels",
                uTurns: "uTurns",
                borderCrossings: "borderCrossings"  // Always include if needed
            };

            const avoidList = Object.entries(avoidOptions)
                .filter(([_, enabled]) => enabled)
                .map(([key]) => avoidMapping[key])
                .filter(Boolean);

            // Convert stop list into waypoints
            const waypointList = stops
                .filter((stop) => stop?.coordinates)
                .map((stop) => ({
                    id: crypto.randomUUID(),
                    type: "break",
                    value: `${stop.coordinates.lat},${stop.coordinates.lon}`
                }));

            const costParameters = buildCostParameters();

            // Payload to send to backend API
            const fake_stops = {
                hazmatType: hazmatType.map(h => h.value),
                waypointOptimization: {
                    optimize: false,
                    lockFirstWaypoint: false,
                    lockLastWaypoint: false
                },
                waypoints: waypointList,
                avoid: avoidList,
                tollProfile: {
                    transponder: true
                },
                routingType: routingType, // Or make dynamic
                vehicleType: "truck",
                vehicleParameters: selectedVehicle.value,
                alternatives: 2,
                ...(costParameters && { costParameters }) // ✅ only added when valid
            };

            // Plan the route
            const response = await LaneService.planRoute(fake_stops, token);
            setRouteResult(response);

        } catch (error) {
            console.error("Route planning failed:", error);
            setRouteResult({ error: "Failed to plan route" });
        } finally {
            setIsPlanning(false);
        }
    };

    const [routingType, setRoutingType] = useState("fastest"); // "fastest" = Quickest routing type

    // Function to fetch fuel prices for selected stops
    const fetchFuelPrices = useCallback(async () => {
        console.log("fetchFuelPrices called");
        const validStops = stops.filter(stop => {
            if (!stop) return false;
            console.log("Stop object in fetchFuelPrices:", stop);
            console.log("Stop city:", stop.city);
            console.log("Stop state:", stop.state);
            console.log("Stop displayName:", stop.displayName);
            console.log("Stop address:", stop.address);
            // For now, let's use any stop that has basic location info
            return stop.city || stop.displayName;
        });
        console.log("Valid stops with zip codes:", validStops);
        if (validStops.length < 2) {
            setFuelPrices([]);
            setCalculatedFuelPrice("");
            return;
        }

        setIsLoadingFuel(true);
        console.log("Starting fuel price fetch for stops:", validStops.map(s => s.displayName));
        try {
            const token = await getAccessTokenSilently();
            
            // Use the new cityFuelSearchByCityState API with separate city and state parameters
            if (validStops.length >= 2) {
                const stop1 = validStops[0];
                const stop2 = validStops[1];
                console.log("Using cityFuelSearchByCityState for stops:", stop1.displayName, "and", stop2.displayName);
                
                try {
                    // State abbreviation to full name mapping
                    const stateMapping = {
                        'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                        'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
                        'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
                        'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                        'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
                        'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
                        'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
                        'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                        'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                        'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
                    };
                    
                    // Extract city and state for each stop
                    const city1 = stop1.city || stop1.displayName;
                    const state1Abbr = stop1.state || stop1.address?.stateCode;
                    const state1 = stateMapping[state1Abbr] || state1Abbr;
                    
                    const city2 = stop2.city || stop2.displayName;
                    const state2Abbr = stop2.state || stop2.address?.stateCode;
                    const state2 = stateMapping[state2Abbr] || state2Abbr;
                    
                    console.log("Stop 1 - City:", city1, "State:", state1, "(from", state1Abbr, ")");
                    console.log("Stop 2 - City:", city2, "State:", state2, "(from", state2Abbr, ")");
                    
                    // Make API calls for both locations using separate city and state parameters
                    const [response1, response2] = await Promise.all([
                        UserService.cityFuelSearchByCityState(city1, state1, token).catch(err => {
                            console.error("Failed to fetch fuel for stop 1:", err);
                            return null;
                        }),
                        UserService.cityFuelSearchByCityState(city2, state2, token).catch(err => {
                            console.error("Failed to fetch fuel for stop 2:", err);
                            return null;
                        })
                    ]);
                    
                    console.log("Fuel API response 1:", response1?.data);
                    console.log("Fuel API response 2:", response2?.data);
                    
                    // Extract fuel prices from responses
                    const prices = [];
                    
                    // Process response 1 - calculate average from all gas station prices
                    if (response1?.data?.gas_prices && response1.data.gas_prices.length > 0) {
                        const stationPrices = [];
                        response1.data.gas_prices.forEach((gasPrice, index) => {
                            if (gasPrice.price) {
                                // Remove $ sign and parse the price
                                const priceStr = gasPrice.price.replace('$', '');
                                const price = parseFloat(priceStr);
                                if (!isNaN(price)) {
                                    stationPrices.push(price);
                                    console.log(`Stop 1 - Station ${index + 1} (${gasPrice.station}): $${price}`);
                                }
                            }
                        });
                        
                        if (stationPrices.length > 0) {
                            const averagePrice = stationPrices.reduce((sum, price) => sum + price, 0) / stationPrices.length;
                            prices.push(averagePrice);
                            console.log("Stop 1 average fuel price:", averagePrice.toFixed(2), "from", stationPrices.length, "stations");
                        }
                    }
                    
                    // Process response 2 - calculate average from all gas station prices
                    if (response2?.data?.gas_prices && response2.data.gas_prices.length > 0) {
                        const stationPrices = [];
                        response2.data.gas_prices.forEach((gasPrice, index) => {
                            if (gasPrice.price) {
                                // Remove $ sign and parse the price
                                const priceStr = gasPrice.price.replace('$', '');
                                const price = parseFloat(priceStr);
                                if (!isNaN(price)) {
                                    stationPrices.push(price);
                                    console.log(`Stop 2 - Station ${index + 1} (${gasPrice.station}): $${price}`);
                                }
                            }
                        });
                        
                        if (stationPrices.length > 0) {
                            const averagePrice = stationPrices.reduce((sum, price) => sum + price, 0) / stationPrices.length;
                            prices.push(averagePrice);
                            console.log("Stop 2 average fuel price:", averagePrice.toFixed(2), "from", stationPrices.length, "stations");
                        }
                    }
                    
                    console.log("Extracted fuel prices:", prices);
                    setFuelPrices(prices);
                    
                    if (prices.length > 0) {
                        const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
                        console.log("Calculated average fuel price:", averagePrice);
                        setCalculatedFuelPrice(averagePrice.toFixed(2));
                        setCostParams(prev => ({ ...prev, fuelPrice: averagePrice.toFixed(2) }));
                    } else {
                        console.log("No valid fuel prices found in responses");
                        setCalculatedFuelPrice("");
                    }
                } catch (error) {
                    console.error("Failed to fetch fuel prices with cityFuelSearchByCityState:", error);
                    setCalculatedFuelPrice("");
                }
            }
        } catch (error) {
            console.error("Failed to fetch fuel prices:", error);
            setFuelPrices([]);
            setCalculatedFuelPrice("");
        } finally {
            setIsLoadingFuel(false);
        }
    }, [stops, getAccessTokenSilently]);

    // Effect to fetch fuel prices when stops change
    useEffect(() => {
        console.log("Stops changed:", stops);
        console.log("First stop details:", stops[0]);
        console.log("Second stop details:", stops[1]);
        
        // Check what properties the stops actually have
        const validStops = stops.filter(stop => {
            if (!stop) return false;
            console.log("Stop properties:", Object.keys(stop));
            console.log("Stop object:", stop);
            // Use any stop that has basic location info (city or displayName)
            return stop.city || stop.displayName;
        });
        
        console.log("Valid stops with zip codes:", validStops);
        if (validStops.length >= 2) {
            console.log("Fetching fuel prices for", validStops.length, "stops using zip codes");
            fetchFuelPrices();
        } else {
            console.log("Not enough stops with zip codes, clearing fuel prices");
            setFuelPrices([]);
            setCalculatedFuelPrice("");
        }
    }, [stops, fetchFuelPrices]);




    const inputBoxStyle = {
        width: "170px",
        padding: "12px 4px 12px 16px"
    };
    const selectedStopsCount = stops.filter((s) => s !== null).length;

    // --- DOWNLOAD PDF ---

    const downloadAsPDF = async () => {

        // Clone the content and fix width for PDF
        const source = document.getElementById("route-results");
        const element = document.createElement("div");        
        element.innerHTML = source.innerHTML;
        element.style.width = "100%";        
        element.classList.toggle('pdf-format');

        // Remove map container
        const mapContainer = element.querySelector('#map-wrapper');
        if (mapContainer) {
            mapContainer.remove();
        }
        
        // Now add a clean map at the top
        if (routeResult?.data?.legs) {
            try {
                // Wait for map to be fully rendered
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Use html2canvas to capture the map as an image
                const mapElement = document.querySelector('#google-map');
                if (mapElement) {
                    const canvas = await html2canvas(mapElement, {
                        scale: 1,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: 'transparent',
                        // width: mapElement.scrollWidth,
                        // height: mapElement.scrollHeight,
                        width: mapElement.width,
                        height: mapElement.height,
                        scrollX: 0,
                        scrollY: 0
                    });
                    
                    // Convert canvas to data URL
                    const mapImageData = canvas.toDataURL('image/png');
                    
                    // Create a clean map image
                    const mapImg = document.createElement('img');
                    mapImg.src = mapImageData;
                    mapImg.style.cssText = `
                        width:100%;
                        display: block;
                        margin: 0;
                        border: 1px soli #aaa;
                        background: transparent;
                        object-fit: contain;
                    `;
                    
                    // Insert map at the very top of the PDF content
                    element.insertBefore(mapImg, element.firstChild);
                }
            } catch (error) {
                console.error('Error capturing map:', error);
                // Continue without map if there's an error
            }
        }
        
        // Add pdf content to screen (?)
        document.body.appendChild(element);

        const startStop = stops[0]?.displayName?.replace(/\s+/g, '') || 'Start';
        const endStop = stops[stops.length - 1]?.displayName?.replace(/\s+/g, '') || 'End';
        const now = new Date();
        const timestamp = now.toISOString().replace(/T/, '_').replace(/:/g, '-').replace(/\..+/, '');
        const filename = `FareLanes_Route_${startStop}_to_${endStop}_${timestamp}.pdf`;

        const opt = {
            margin: 0.5,
            filename: filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        };

        await html2pdf().set(opt).from(element).save();

        // Remove pdf content from screen (?)
        document.body.removeChild(element); // 🧹 Clean up
    };

    // --- Generate PDF as Blob (for email sending) --- 

    const generatePDFBlob = async () => {
        const source = document.getElementById("route-results");
        const element = document.createElement("div");
        element.innerHTML = source.innerHTML;
        element.style.width = "100%";        
        element.classList.toggle('pdf-format');
        
        // Remove map container (same as download function)
        const mapContainer = element.querySelector('#map-wrapper');
        if (mapContainer) mapContainer.remove();
        
        // const otherMapElements = element.querySelectorAll('[id*="map"], [class*="map"], [class*="Map"], [id*="Map"]');
        // otherMapElements.forEach(el => el.remove());
        
        // const googleMapElements = element.querySelectorAll('.gm-style, .gm-control-active, .gm-svpc, .gm-bttn, .gm-style-pbc, .gm-style-pbt');
        // googleMapElements.forEach(el => el.remove());
        
        // const allDivs = element.querySelectorAll('div');
        // allDivs.forEach(div => {
        //     const style = window.getComputedStyle(div);
        //     if (
        //         style.height === '400px' || style.height === '300px' || style.height === '500px' ||
        //         div.style.height === '400px' || div.style.height === '300px' || div.style.height === '500px'
        //     ) {
        //         div.remove();
        //     }
        // });
        
        // Capture map if available
        if (routeResult?.data?.legs) {
            try {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const mapElement = document.querySelector('#google-map');
                if (mapElement) {
                    const canvas = await html2canvas(mapElement, {
                        scale: 1,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: 'transparent',
                        // width: mapElement.scrollWidth,
                        // height: mapElement.scrollHeight,
                        width: mapElement.width,
                        height: mapElement.height,
                        scrollX: 0,
                        scrollY: 0
                    });
                    
                    const mapImageData = canvas.toDataURL('image/png');
                    const mapImg = document.createElement('img');
                    mapImg.src = mapImageData;
                    mapImg.style.cssText = `
                        width:100%;
                        display: block;
                        margin: 0;
                        border: 1px solid #aaa;
                        background: transparent;
                        object-fit: contain;
                        
                        // width: 95%;
                        // max-width: 900px;
                        // height: auto;
                        // margin: 20px auto;
                        // border: none;
                    `;
                    element.insertBefore(mapImg, element.firstChild);
                }
            } catch (error) {
                console.error('Error capturing map:', error);
            }
        }
        
        // Add pdf content to screen (?)
        document.body.appendChild(element);

        const opt = {
            margin: 0.5,
            filename: 'route.pdf',
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        };

        const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');

        // Remove pdf content from screen (?)
        document.body.removeChild(element);
        
        return pdfBlob;
    };

    // Convert blob to base64
    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
                resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    // Build a Google Maps route URL from current stops
    const buildGoogleMapsRouteUrl = () => {
        const validStops = stops.filter((s) => s);
        if (validStops.length < 2) return "";

        const formatLocation = (s) => {
            if (s?.coordinates?.lat != null && s?.coordinates?.lon != null) {
                return `${s.coordinates.lat},${s.coordinates.lon}`;
            }
            return s?.displayName || "";
        };

        const origin = encodeURIComponent(formatLocation(validStops[0]));
        const destination = encodeURIComponent(formatLocation(validStops[validStops.length - 1]));
        const midWaypoints = validStops.slice(1, -1).map(formatLocation);
        const waypointsParam = midWaypoints.length
            ? `&waypoints=${encodeURIComponent(midWaypoints.join('|'))}`
            : "";

        return `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${origin}&destination=${destination}${waypointsParam}`;
    };

    // Send PDF via email
    const sendPDFByEmail = async () => {
        if (!emailAddress || !emailAddress.includes('@')) {
            alert('Please enter a valid email address');
            return;
        }

        // If user entered a phone, ensure it starts with +1 or +91
        const trimmedPhone = (recipientPhone || "").trim();
        if (trimmedPhone && !/^\+(1|91)\d*$/.test(trimmedPhone)) {
            alert('Please enter a phone number starting with +1 or +91');
            return;
        }

        setIsSendingEmail(true);
        try {
            // 1. Generate PDF as blob
            const pdfBlob = await generatePDFBlob();
            
            // 2. Convert to Base64
            const pdfBase64 = await blobToBase64(pdfBlob);
            
            // 3. Prepare email data
            const startStop = stops[0]?.displayName || 'Start';
            const endStop = stops[stops.length - 1]?.displayName || 'End';
            const mapsUrl = buildGoogleMapsRouteUrl();
            
            const emailData = {
                recipientEmail: emailAddress,
                recipientPhone: trimmedPhone,
                recipientEmailMessage: `This is your route: ${mapsUrl}`,
                pdfBase64: pdfBase64,
                routeDetails: {
                    from: startStop,
                    to: endStop,
                    distance: routeResult?.data?.totalDistance,
                    duration: routeResult?.data?.totalDuration
                }
            };
            
            // 4. Call Java API
            const token = await getAccessTokenSilently();
            await LaneService.sendRouteEmail(emailData, token);
            
            alert(`Route plan sent successfully to ${emailAddress}!`);
            setEmailAddress(""); // Clear email input
            setRecipientPhone("+1");
        } catch (error) {
            console.error('Failed to send email:', error);
            alert('Failed to send email. Please try again.');
        } finally {
            setIsSendingEmail(false);
        }
    };


    return (
        <div className="dashboard-text container-fluid plan-route-container">

            <div>

            	{/* --- ROUTE SETTINGS --- */}

				<h5 id="route-settings" className="milemaker-h5">
					Step 1: Route Settings
				</h5>
                        
				<label className="form-label">
                   	Please select items you want to avoid
                </label>
                        
                <div className="milemaker-box">
                	Toggle switches to avoid specific road types or conditions. This helps customize your route based on your vehicle type and preferences.
                </div>

                <div className="toggle-switches">
                    {Object.entries(avoidOptions).map(([key, value]) => (
                        <div
                            key={key}
                            className="toggle-switch"
                            onClick={() =>
                            	setAvoidOptions(prev => ({
                                	...prev,
                                    [key]: !prev[key]
                                }))
                            }
                        >
                            <div className={`toggle-switch-control ${value ? 'active' : ''}`}>
                                <div className="toggle-switch-slider" />
                            </div>

                            <span className="toggle-switch-label">
                                {key.replace(/([A-Z])/g, ' $1')}
                            </span>

                        </div>
                    ))}
                </div>

                {/* --- VEHICLE TYPE & HAZMAT SETTINGS --- */}

				<h5 id="vehicle-type" className="milemaker-h5">
					Step 2: Vehicle Type &amp; Hazmat Settings
				</h5>

                <div className="row">

                    <div className="col-6"><div className="milemaker-extra-box">
                        
                        <h6 className="milemaker-h6">
                        	Vehicle Type
                        </h6>

                        <div className="milemaker-box">
                            Select your vehicle type to calculate accurate route costs and restrictions
                        </div>

                        <Select
                            styles={{
                                ...selectStyles,
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: '45px',
                                })
                            }}
                            options={vehicleOptions}
                            value={selectedVehicle}
                            onChange={(selectedOption) => setSelectedVehicle(selectedOption)}
                            getOptionLabel={(e) => (
                                <div style={{ whiteSpace: "pre-line" }}>
                                    <span className="milemaker-vehicle-type">{e.label}</span>
									({e.value.height}" H × {e.value.width}" W × {e.value.length} L)
                                </div>
                            )}
                            getOptionValue={(e) => e.label} // This makes search work!
                        />
                    </div></div>

                    {/* Hazmat Multi-Select */}

                    <div className="col-6"><div className="milemaker-extra-box">
                        
                        <h6 className="milemaker-h6">
                        	Hazardous Material Type
                        </h6>

                        <div className="milemaker-box">
                            Select all hazardous materials you're transporting (affects routing and regulations)
                        </div>
                        
                        <Select
                            styles={{
                                ...selectStyles,
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: '45px',
                                })
                            }}
                            isMulti
                            options={hazmatOptions}
                            value={hazmatType}
                            onChange={handleHazmatChange}
                            placeholder="Select hazmat types..."
                            closeMenuOnSelect={false}
                        />
                    </div></div>
                    
                </div>

				{/* --- FIXED COSTS --- */}

            	<h5 id="fixed-costs" className="milemaker-h5">
            		Step 3: Fixed Costs
            	</h5>

                <label className="form-label">
                	Get data on fuel costs and other costs of the route
                </label>

                <div className="row">

                    {/* Fuel Parameters */}

                    <div className="col-6"><div className="milemaker-extra-box">
                        
                        <h6 className="milemaker-h6">Fuel Parameters</h6>

                        {/* Fuel Price Section */}

                        <div style={{ marginBottom: "24px" }}>

							{/* Friendly message when not enough stops */}
                        	{stops.filter(stop => stop && stop.displayName).length < 2 && (
                        		<div className="milemaker-box">
                        	    	Add at least 2 stops in "Step 4: Routing" so our real-time fuel engine can automatically populate fuel prices for your route!
                        	    </div>
                        	)}

                            {isLoadingFuel && (
                                <div className="milemaker-spinner-wrap">
                                	<Spinner
                                    	as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Fetching fuel prices...
                                </div>
                            )}
                            
                            {calculatedFuelPrice && !isLoadingFuel && (
                            	<div className="milemaker-results">
                                    Using our live fuel API, we estimated average fuel price across the route ({stops.length >= 2 ? `${stops[0]?.city || stops[0]?.displayName || 'Start'}, ${stops[0]?.state || stops[0]?.address?.stateCode || 'State'} → ${stops[stops.length - 1]?.city || stops[stops.length - 1]?.displayName || 'End'}, ${stops[stops.length - 1]?.state || stops[stops.length - 1]?.address?.stateCode || 'State'}` : 'Route'}): $${calculatedFuelPrice}/gallon
                                </div>
                            )}
                            
                            <div style={{ 
                            	display: "grid", 
                                gridTemplateColumns: "200px auto", 
                                gap: "12px", 
                                alignItems: "center"                             
                            }}>                            
                            	<input
									type="number"
									placeholder={isLoadingFuel ? "Loading fuel prices..." : "Enter fuel price"}
									className="form-control"
									style={{
                                        ...inputBoxStyle,
                                        width: "100%"
									}}
									value={costParams.fuelPrice}
									onChange={(e) =>
                                    	setCostParams((prev) => ({ ...prev, fuelPrice: e.target.value }))
                                	}
                                	disabled={isLoadingFuel}
                            	/>
                            	<span className="milemaker-field-suffix">USD/gallon</span>
                            </div>

                        </div>

                        {/* Fuel Economy */}

                        <div>

                            <div className="milemaker-box">
                                Enter your vehicle's fuel efficiency (miles per gallon) for accurate cost calculation
                            </div>
                            
                            <div style={{ 
                                display: "grid", 
                                gridTemplateColumns: "200px auto", 
                                gap: "12px", 
                                alignItems: "center" 
                            }}>
                            	<input
                                	type="number"
                                    placeholder="Enter fuel economy"
                                	className="form-control"
                                    style={{
                                        ...inputBoxStyle,
                                     	width: "100%"
                                    }}
                                	value={costParams.fuelEconomy}
                                	onChange={(e) =>
                                	    setCostParams((prev) => ({ ...prev, fuelEconomy: e.target.value }))
                                	}
                                	disabled={!costParams.fuelPrice || isNaN(parseFloat(costParams.fuelPrice))}
                            	/>
                            	<span className="milemaker-field-suffix">mi/gallon</span>
                            </div>

                        </div>

                    </div></div>

                    {/* Other Costs Section */}

                    <div className="col-6"><div className="milemaker-extra-box">
                        
                        <h6 className="milemaker-h6">Other Costs</h6>

                        {/* Maintenance Cost */}

                        <div style={{ marginBottom: "24px" }}>

	                        <div className="milemaker-box">
	                        	Enter maintenance cost per mile (includes repairs, oil changes, tire wear, etc.)
	                        </div>

	                        <div style={{ 
                            	display: "grid", 
                                gridTemplateColumns: "200px auto", 
                                gap: "12px", 
                                alignItems: "center" 
                            }}>
                            
                            	<input
                            	    type="number"
                            	    placeholder="Enter maintenance cost"
                            	    className="form-control"
                            	    style={{
                            	        ...inputBoxStyle,
                            	 		width: "100%"
                            	    }}
                            	    value={costParams.maintenanceCost}
                            	    onChange={(e) => setCostParams(prev => ({ ...prev, maintenanceCost: e.target.value }))}
                            	/>
                            
                            	<span className="milemaker-field-suffix">USD/mi</span>
                        	</div>

	                    </div>

                        {/* Labor Cost */}

	                        <div className="milemaker-box">
	                        	Enter driver labor cost per hour (includes wages, benefits, and driver-related expenses)
	                        </div>

							<div style={{ 
                                    display: "grid", 
                                    gridTemplateColumns: "200px auto", 
                                    gap: "12px", 
                                    alignItems: "center" 
                                }}>
                            	<input
                                type="number"
                                        placeholder="Enter labor cost"
                                className="form-control"
                                        style={{
                                            ...inputBoxStyle,
                                            width: "100%"
                                        }}
                                value={costParams.laborCost}
                                onChange={(e) => setCostParams(prev => ({ ...prev, laborCost: e.target.value }))}
                            	/>
                                    <span style={{ 
                                        fontSize: "14px", 
                                        color: "#6c757d",
                                        fontWeight: "500",
                                        minWidth: "80px"
                                    }}>
                                        USD/hour
                                    </span>
                                </div>                        

                        	<div>
	                    </div>
                        
                    </div></div>

                </div>

                {/* Toll Setting */}

                <div className="row">

                    {/* Fuel Parameters */}

                    <div className="col-12">
                    	<div className="form-check" style={{ marginBottom: "10px" }}>
                        	<input
                        	    className="form-check-input"
                        	    type="checkbox"
                        	    id="transponderToll"
                        	    checked={true}
                        	    onChange={() => {
                        	        // TODO: handle checkbox state
                        	    }}
                        	/>
                        	<label className="form-check-label" htmlFor="transponderToll">
                        	    Use transponder toll rates
                        	</label>
                    	</div>
                    </div>

                </div>                
				
				{/* --- ROUTING ---*/}

				<h5 id="stops" className="milemaker-h5">Step 4: Routing</h5>

                <div className="row">
                    <div className="col-12">
                        <div className="form-section">
                            <div className="routing-type-section">
                                
                                <label className="form-label">Routing Type</label>
	                        	<div className="milemaker-box">
                                    Choose your preferred routing method to optimize your route based on your priorities (time vs distance)
                                </div>

                                <div className="routing-radio-group">
                                    
                                    <div className="routing-radio-option">
                                        <input
                                            type="radio"
                                            id="routing-practical"
                                            name="routingType"
                                            value="practical"
                                            checked={routingType === "practical"}
                                            onChange={(e) => setRoutingType(e.target.value)}
                                            className="routing-radio-input"
                                        />
                                        <label htmlFor="routing-practical" className="routing-radio-label">
                                            <div className="routing-option-title">Practical routing type</div>
                                            <div className="routing-option-description">Mileage Rating, optimized for time</div>
                                        </label>
                                    </div>
                                    
                                    <div className="routing-radio-option">
                                        <input
                                            type="radio"
                                            id="routing-fastest"
                                            name="routingType"
                                            value="fastest"
                                            checked={routingType === "fastest"}
                                            onChange={(e) => setRoutingType(e.target.value)}
                                            className="routing-radio-input"
                                        />
                                        <label htmlFor="routing-fastest" className="routing-radio-label">
                                        	<div className="routing-option-title">Quickest routing type</div>
                                        	<div className="routing-option-description">Trip Planning, optimized for time</div>
                                        </label>
                                    </div>
                                    
                                    <div className="routing-radio-option">
                                        <input
                                            type="radio"
                                            id="routing-shortest"
                                            name="routingType"
                                            value="shortest"
                                            checked={routingType === "shortest"}
                                            onChange={(e) => setRoutingType(e.target.value)}
                                            className="routing-radio-input"
                                        />
                                        <label htmlFor="routing-shortest" className="routing-radio-label">
                                            <div className="routing-option-title">HHG routing type</div>
                                            <div className="routing-option-description">Mileage Rating, optimized for distance</div>
                                        </label>
									</div>
                                </div>
                            </div>

                            {/*Fuell*/}
                            <div>

                            </div>

                        </div>
                    </div>

                    {/* --- SELECT STOPS --- */}

                    <div className="col-12 flex-column">
                        <div className="flex-row">
                            <div className="flex-grow">

                                <label className="form-label">Select Stops</label>

                                {stops.map((selectedValue, index) => (
                                    <div key={index} className="stop-item">
                                        <div className="stop-header">
                                            {stops.length > 1 && (
                                                <button
                                                    onClick={() => handleRemoveStop(index)}
                                                    className="btn-remove-stop"
                                                    title="Remove Stop"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            )}
                                            <div className="stop-content">
                                                <div className="async-select-container">
                                                    <AsyncSelect
                                                        cacheOptions
                                                        styles={selectStyles}
                                                        defaultOptions
                                                        value={selectedValue}
                                                        getOptionValue={(e) => e.splc || e.displayName}
                                                        getOptionLabel={(e) => (
                                                            <div style={{ lineHeight: 1.5 }}>
                                                                <div style={{ fontWeight: "600" }}>{e.displayName}</div>
                                                                <div style={{ fontSize: "1.3rem", color: "#6c757d" }}>
                                                                    {e.address?.city}, {e.address?.stateCode}, {e.address?.postalCode}
                                                                </div>
                                                                {e.address?.county && (
                                                                    <div style={{ fontSize: "1.2rem", color: "#aaa" }}>
                                                                        County: {e.address.county}
                                                                    </div>
                                                                )}
                                                                {e.coordinates && (
                                                                    <div style={{ fontSize: "1.2rem", color: "#888" }}>
                                                                        <strong>Lat:</strong> {e.coordinates.lat} | <strong>Long:</strong> {e.coordinates.lon}
                                                                    </div>
                                                                )}
                                                                {e.splc && (
                                                                    <div style={{ fontSize: "1.2rem", color: "#888" }}>
                                                                        <strong>SPLC:</strong> {e.splc}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        loadOptions={loadOptions}
                                                        onChange={(val) => handleStopChange(val, index)}
                                                        placeholder={`Select Stop #${index + 1}`}
                                                        isClearable
                                                    />
                                                </div>

                                                {/* Insert the "+" add button after the last stop only, and PLAN ROUTE button beside it */}
                                                {index === stops.length - 1 && (
                                                    <>                                                       

                                                        <button onClick={handleAddStop} type="button" className="my-button" title="Add Stop">
                                                            ADD STOP
                                                        </button>

                                                        {selectedStopsCount >= 2 && stops[stops.length - 1] !== null && (
                                                            <>

                                                                <button
                                                                    onClick={handlePlanRoute}
                                                                    type="button"
                                                                    disabled={isPlanning}
                                                                    className="my-button green"
                                                                >
                                                                    {isPlanning ? (
                                                                        <>
                                                                            <Spinner
                                                                                as="span"
                                                                                animation="border"
                                                                                size="sm"
                                                                                role="status"
                                                                                aria-hidden="true"
                                                                                style={{ marginRight: "8px" }}
                                                                            />
                                                                            Planning Route...
                                                                        </>
                                                                    ) : (
                                                                        "PLAN ROUTE"
                                                                    )}
                                                                </button>


                                                                
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
				
				{/* --- RESULTS ---*/}

                {routeResult && routeResult.data && (

                    <>                

						<h5 id="stops" className="milemaker-h5">Route Results</h5>

						<div className="row">

                        	<div className="col-4">
                            	<div className="milemaker-extra-box">

                                	<div>
                                		<label>Download Results</label>
                                	</div>
                                    <button onClick={() => downloadAsPDF()} className="my-button">
                                    	Download PDF
                                    </button>
                                
                                </div>                            
                            </div>

                            <div className="col-4">
                            	<div className="milemaker-extra-box">

                                	<label>Email Results</label>
                                	<input
										type="email"
										placeholder="Enter email address"
										value={emailAddress}
										onChange={(e) => setEmailAddress(e.target.value)}
										className="form-control"
                                        disabled={isSendingEmail}
                                    />
                                                                        
                                    <button
										onClick={sendPDFByEmail}
										className="my-button margin-top-1rem"
										disabled={isSendingEmail || !emailAddress}
                                    >
                                        {isSendingEmail ? (
                            				<span className="spinner-border spinner-border-sm me-2"></span>
                                        ) : (
	                                    	<span>Send</span>
    	                                )}
        	                        </button>
                                
                                </div>
                            </div>

                            <div className="col-4">
                            	<div className="milemaker-extra-box">
                                
                                	<label>Text Results</label>
                                    <input
										type="tel"
										placeholder="Enter phone (optional)"
										value={recipientPhone}
										onChange={(e) => setRecipientPhone(e.target.value)}
										className="form-control"
                                        // disabled={isSendingEmail}
                                    />                                                                        
                                    <button 
                                    	className="my-button margin-top-1rem"
										disabled={isSendingEmail || !emailAddress}
									>Send</button>
                                
                                </div>
                            </div>
                        
                        </div>

					</>
					
				)}

                {routeResult && routeResult.data && (
                
                	<div id="route-results">

                        <div id="map-wrapper" className="margin-line-40px">
                        	<MapComponent encodedShapes={routeResult.data.legs.map(leg => leg.shape)} />
	                    </div>
        	        	
                        <div className="margin-line-40px">
                	        <StateBreakdownReportTable data={routeResult.data.stateBreakdown} /> 
        	            </div>     

        	        	<div className="row margin-line-40px">

            	            <RouteSummary 
                    	       	routeData={routeResult.data} 
                        	    stops={stops}
                                selectedVehicle={selectedVehicle}
	                        />
    	                    
    	                    <div className="col-12 col-sm-4">
            	                <RouteSettings
                	            	routingType={routingType}
                    	            stops={stops}
                        	        avoidOptions={avoidOptions}
                            	    selectedVehicle={selectedVehicle}
                                	hazmatOptions={hazmatType}
	                            />
    	                    </div>
                        
        	            </div>                  
    	                

                        <div className="margin-line-40px break-before-page">
            	    		<RouteItineraryReportTable legs={routeResult.data.legs} stops={stops} />
        	            </div>                       
                    	
                    </div>

                )}



			</div>

        </div>
    );
};

export default withAuthenticationRequired(PlanRoute, {
    onRedirecting: () => <PageLoader />,
});